import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Copy, User, Key, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [transaction, setTransaction] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const order = searchParams.get('order');

  useEffect(() => {
    let cancelled = false;
    const checkTransbank = async () => {
      try {
        const res = await fetch(`${API_URL}/api/transbank/status/${order}`);
        if (!res.ok) throw new Error('No se pudo verificar la transacción');
        const data = await res.json();
        if (cancelled) return;
        setTransaction(data);
        if (data.status === 'paid') setStatus('success');
        else if (data.status === 'pending') setTimeout(() => { if (!cancelled) checkTransbank(); }, 2000);
        else { setStatus('error'); setError(`Estado del pago: ${data.status}`); }
      } catch (e) {
        if (!cancelled) { setStatus('error'); setError(e.message || 'Error al verificar el pago'); }
      }
    };
    const checkStripe = async () => {
      try {
        const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
        const data = await response.json();
        if (cancelled) return;
        if (data.payment_status === 'paid') setStatus('success');
        else if (data.payment_status === 'pending') setTimeout(() => { if (!cancelled) checkStripe(); }, 2000);
        else { setStatus('error'); setError(`Estado del pago: ${data.payment_status}`); }
      } catch (err) {
        if (!cancelled) { setStatus('error'); setError('Error al verificar el pago'); }
      }
    };

    if (order) checkTransbank();
    else if (sessionId) checkStripe();
    else { setStatus('error'); setError('No se encontró la referencia de pago'); }
    return () => { cancelled = true; };
  }, [order, sessionId]);

  const copy = (v) => {
    if (!v) return;
    navigator.clipboard.writeText(v);
    toast.success('Copiado al portapapeles');
  };

  const creds = transaction?.provisioned_credentials;
  const isStudent = transaction?.tipo === 'student_subscription';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full bg-slate-800 border-slate-700">
        <CardContent className="pt-8 pb-8">
          {status === 'loading' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 mx-auto text-cyan-500 animate-spin mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">Verificando pago...</h2>
              <p className="text-slate-400">Por favor espera mientras confirmamos tu pago</p>
            </div>
          )}

          {status === 'success' && (
            <>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">¡Pago exitoso!</h2>
                <p className="text-slate-400 mb-6">
                  {isStudent
                    ? 'Tu suscripción del plan Estudiante ha sido activada.'
                    : 'Tu pago ha sido procesado correctamente. Tu suscripción está activa.'}
                </p>
                {transaction && (
                  <div className="text-slate-400 text-xs mb-6">
                    Orden {transaction.buy_order} · {transaction.monto_clp_formateado}
                  </div>
                )}
              </div>

              {creds && (
                <div className="mb-6 p-4 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/30">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={16} className="text-cyan-400" />
                    <span className="text-cyan-400 text-sm font-semibold">
                      Tus datos de acceso (guárdalos)
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <User size={14} className="text-slate-400 shrink-0" />
                        <span className="text-white text-sm truncate">{creds.email}</span>
                      </div>
                      <button onClick={() => copy(creds.email)} className="text-slate-400 hover:text-white shrink-0" data-testid="copy-email">
                        <Copy size={14} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Key size={14} className="text-slate-400 shrink-0" />
                        <span className="text-white text-sm font-mono truncate">{creds.temp_password}</span>
                      </div>
                      <button onClick={() => copy(creds.temp_password)} className="text-slate-400 hover:text-white shrink-0" data-testid="copy-password">
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                  <p className="text-amber-300/80 text-[11px] mt-3">
                    ⚠ Esta contraseña temporal solo se muestra una vez. Guárdala y cámbiala al iniciar sesión.
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button 
                  onClick={() => navigate(isStudent ? '/login' : '/dashboard')}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
                  data-testid="go-to-app"
                >
                  {isStudent ? 'Iniciar sesión' : 'Ir al Dashboard'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Volver al inicio
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Error en el pago</h2>
              <p className="text-slate-400 mb-6">
                {error || 'Hubo un problema al procesar tu pago'}
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/#planes')}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
                >
                  Intentar de nuevo
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  Volver al inicio
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
